import { TagKey } from '@cof/tag-catalog-refi-user-entry-mfe';
import { DebugService } from '../debug/debug.service.js';
import SessionService from '../session/session.service.js';
import { EXPRESS_RETURN_CONST } from '../../constants/constants.js';
import { stratum } from '../../constants/stratum.js';

/**
 * Service for handling express return routing logic
 * Manages routing for customers with real appId and templateId values
 */
class _ExpressReturnRoutingService {
  constructor() {
    this.debug = new DebugService('ExpressReturnRoutingService');
  }

  /**
   * Handles express return routing for customers with real appId and templateId
   * @param {Object} parsedReturnCust - Parsed return customer data
   * @param {string} returnCustParamValue - Original returnCust parameter value
   * @param {Function} isFeatureEnabled - Feature toggle checker function
   * @returns {boolean} - True if routing was handled, false if no routing occurred
   */
  handleExpressReturnRouting(parsedReturnCust, returnCustParamValue, isFeatureEnabled) {
    // Validate inputs
    if (!parsedReturnCust || !returnCustParamValue || typeof isFeatureEnabled !== 'function') {
      this.debug.log('handleExpressReturnRouting: invalid input parameters');
      return false;
    }

    // Check if express return routing is applicable
    if (!parsedReturnCust.isExpressReturn) {
      this.debug.log('handleExpressReturnRouting: not an express return case');
      return false;
    }

    // Fire analytics tag for express return user routed from customer journey
    // Fire before feature toggle check to track all attempts, toggle will be removed eventually
    stratum.publishTag(TagKey.EXPRESS_RETURN_USER_ROUTED_FROM_CUSTOMER_JOURNEY, {
      replacements: { value: returnCustParamValue },
    });

    if (!isFeatureEnabled(EXPRESS_RETURN_CONST.EXPRESS_RETURN)) {
      this.debug.log('handleExpressReturnRouting: express return feature disabled');
      return false;
    }

    this.debug.log('handleExpressReturnRouting: express return detected, storing data');

    try {
      // Store the original returnCust value in session for later use
      SessionService.setExpressEntryReturnCustValue(returnCustParamValue);

      return true;
    } catch (error) {
      this.debug.log('handleExpressReturnRouting: error occurred during storage', error);
      return false;
    }
  }

  /**
   * Checks if express return routing is applicable for the given parsed data
   * @param {Object} parsedReturnCust - Parsed return customer data
   * @param {Function} isFeatureEnabled - Feature toggle checker function
   * @returns {boolean} - True if express return routing can be applied
   */
  canHandleRouting(parsedReturnCust, isFeatureEnabled) {
    if (!parsedReturnCust || typeof isFeatureEnabled !== 'function') {
      return false;
    }

    return parsedReturnCust.isExpressReturn && isFeatureEnabled(EXPRESS_RETURN_CONST.EXPRESS_RETURN);
  }

  /**
   * Validates that the necessary data for express return is present
   * @param {Object} parsedReturnCust - Parsed return customer data
   * @returns {boolean} - True if express return data is valid
   */
  isValidExpressReturnData(parsedReturnCust) {
    if (!parsedReturnCust) {
      return false;
    }

    return Boolean(parsedReturnCust.appId) && Boolean(parsedReturnCust.templateId) && Boolean(parsedReturnCust.isExpressReturn);
  }
}

const ExpressReturnRoutingService = new _ExpressReturnRoutingService();
export default ExpressReturnRoutingService;
